import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i2 from '@angular/common';
import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, PLATFORM_ID, booleanAttribute, numberAttribute, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, Input, Output, ContentChildren, HostListener, NgModule } from '@angular/core';
import * as i1 from 'primeng/api';
import { PrimeTemplate, SharedModule } from 'primeng/api';
import { DomHandler, ConnectedOverlayScrollHandler } from 'primeng/dom';
import { TimesIcon } from 'primeng/icons/times';
import * as i3 from 'primeng/ripple';
import { RippleModule } from 'primeng/ripple';
import { ZIndexUtils } from 'primeng/utils';

/**
 * OverlayPanel is a container component positioned as connected to its target.
 * @group Components
 */
const _c0 = ["*"];
const _c1 = (a0, a1) => ({
  showTransitionParams: a0,
  hideTransitionParams: a1
});
const _c2 = (a0, a1) => ({
  value: a0,
  params: a1
});
function OverlayPanel_div_0_ng_container_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function OverlayPanel_div_0_button_4_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 5);
    i0.ɵɵlistener("click", function OverlayPanel_div_0_button_4_Template_button_click_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.onCloseClick($event));
    })("keydown.enter", function OverlayPanel_div_0_button_4_Template_button_keydown_enter_0_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.hide());
    });
    i0.ɵɵelement(1, "span", 6);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵattribute("title", ctx_r1.buttonTitle)("aria-label", ctx_r1.buttonTitle);
  }
}
function OverlayPanel_div_0_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵlistener("click", function OverlayPanel_div_0_Template_div_click_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onOverlayClick($event));
    })("@animation.start", function OverlayPanel_div_0_Template_div_animation_animation_start_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onAnimationStart($event));
    })("@animation.done", function OverlayPanel_div_0_Template_div_animation_animation_done_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onAnimationEnd($event));
    });
    i0.ɵɵelementStart(1, "div", 2);
    i0.ɵɵlistener("click", function OverlayPanel_div_0_Template_div_click_1_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onContentClick($event));
    })("mousedown", function OverlayPanel_div_0_Template_div_mousedown_1_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onContentClick($event));
    });
    i0.ɵɵprojection(2);
    i0.ɵɵtemplate(3, OverlayPanel_div_0_ng_container_3_Template, 1, 0, "ng-container", 3);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, OverlayPanel_div_0_button_4_Template, 2, 2, "button", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵclassMap(ctx_r1.styleClass);
    i0.ɵɵproperty("ngClass", "p-overlaypanel p-component")("ngStyle", ctx_r1.style)("@animation", i0.ɵɵpureFunction2(12, _c2, ctx_r1.overlayVisible ? "open" : "close", i0.ɵɵpureFunction2(9, _c1, ctx_r1.showTransitionOptions, ctx_r1.hideTransitionOptions)));
    i0.ɵɵattribute("aria-label", ctx_r1.ariaLabel)("aria-labelledBy", ctx_r1.ariaLabelledBy);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.contentTemplate);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.showCloseIcon);
  }
}
let OverlayPanel = /*#__PURE__*/(() => {
  class OverlayPanel {
    document;
    platformId;
    el;
    renderer;
    cd;
    zone;
    config;
    overlayService;
    /**
     * Defines a string that labels the input for accessibility.
     * @group Props
     */
    ariaLabel;
    /**
     * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
     * @group Props
     */
    ariaLabelledBy;
    /**
     * Enables to hide the overlay when outside is clicked.
     * @group Props
     */
    dismissable = true;
    /**
     * When enabled, displays a close icon at top right corner.
     * @group Props
     */
    showCloseIcon;
    /**
     * Inline style of the component.
     * @group Props
     */
    style;
    /**
     * Style class of the component.
     * @group Props
     */
    styleClass;
    /**
     *  Target element to attach the panel, valid values are "body" or a local ng-template variable of another element (note: use binding with brackets for template variables, e.g. [appendTo]="mydiv" for a div element having #mydiv as variable name).
     * @group Props
     */
    appendTo = 'body';
    /**
     * Whether to automatically manage layering.
     * @group Props
     */
    autoZIndex = true;
    /**
     * Aria label of the close icon.
     * @group Props
     */
    buttonTitle;
    /**
     * Base zIndex value to use in layering.
     * @group Props
     */
    baseZIndex = 0;
    /**
     * When enabled, first button receives focus on show.
     * @group Props
     */
    focusOnShow = true;
    /**
     * Transition options of the show animation.
     * @group Props
     */
    showTransitionOptions = '.12s cubic-bezier(0, 0, 0.2, 1)';
    closeOnEscape = true;
    /**
     * Transition options of the hide animation.
     * @group Props
     */
    hideTransitionOptions = '.1s linear';
    /**
     * Callback to invoke when an overlay becomes visible.
     * @group Emits
     */
    onShow = new EventEmitter();
    /**
     * Callback to invoke when an overlay gets hidden.
     * @group Emits
     */
    onHide = new EventEmitter();
    templates;
    container;
    overlayVisible = false;
    render = false;
    isOverlayAnimationInProgress = false;
    selfClick = false;
    documentClickListener;
    target;
    willHide;
    scrollHandler;
    documentResizeListener;
    documentEscapeListener;
    contentTemplate;
    closeIconTemplate;
    destroyCallback;
    overlayEventListener;
    overlaySubscription;
    constructor(document, platformId, el, renderer, cd, zone, config, overlayService) {
      this.document = document;
      this.platformId = platformId;
      this.el = el;
      this.renderer = renderer;
      this.cd = cd;
      this.zone = zone;
      this.config = config;
      this.overlayService = overlayService;
    }
    ngAfterContentInit() {
      this.templates?.forEach(item => {
        switch (item.getType()) {
          case 'content':
            this.contentTemplate = item.template;
            break;
          case 'closeicon':
            this.closeIconTemplate = item.template;
            break;
          default:
            this.contentTemplate = item.template;
            break;
        }
        this.cd.markForCheck();
      });
    }
    bindDocumentClickListener() {
      if (isPlatformBrowser(this.platformId)) {
        if (!this.documentClickListener && this.dismissable) {
          let documentEvent = DomHandler.isIOS() ? 'touchstart' : 'click';
          const documentTarget = this.el ? this.el.nativeElement.ownerDocument : this.document;
          this.documentClickListener = this.renderer.listen(documentTarget, documentEvent, event => {
            if (!this.container?.contains(event.target) && this.target !== event.target && !this.target.contains(event.target) && !this.selfClick) {
              this.hide();
            }
            this.selfClick = false;
            this.cd.markForCheck();
          });
        }
      }
    }
    unbindDocumentClickListener() {
      if (this.documentClickListener) {
        this.documentClickListener();
        this.documentClickListener = null;
        this.selfClick = false;
      }
    }
    /**
     * Toggles the visibility of the panel.
     * @param {Event} event - Browser event
     * @param {Target} target - Target element.
     * @group Method
     */
    toggle(event, target) {
      if (this.isOverlayAnimationInProgress) {
        return;
      }
      if (this.overlayVisible) {
        if (this.hasTargetChanged(event, target)) {
          this.destroyCallback = () => {
            this.show(null, target || event.currentTarget || event.target);
          };
        }
        this.hide();
      } else {
        this.show(event, target);
      }
    }
    /**
     * Displays the panel.
     * @param {Event} event - Browser event
     * @param {Target} target - Target element.
     * @group Method
     */
    show(event, target) {
      target && event && event.stopPropagation();
      if (this.isOverlayAnimationInProgress) {
        return;
      }
      this.target = target || event.currentTarget || event.target;
      this.overlayVisible = true;
      this.render = true;
      this.cd.markForCheck();
    }
    onOverlayClick(event) {
      this.overlayService.add({
        originalEvent: event,
        target: this.el.nativeElement
      });
      this.selfClick = true;
    }
    onContentClick(event) {
      const targetElement = event.target;
      this.selfClick = event.offsetX < targetElement.clientWidth && event.offsetY < targetElement.clientHeight;
    }
    hasTargetChanged(event, target) {
      return this.target != null && this.target !== (target || event.currentTarget || event.target);
    }
    appendContainer() {
      if (this.appendTo) {
        if (this.appendTo === 'body') this.renderer.appendChild(this.document.body, this.container);else DomHandler.appendChild(this.container, this.appendTo);
      }
    }
    restoreAppend() {
      if (this.container && this.appendTo) {
        this.renderer.appendChild(this.el.nativeElement, this.container);
      }
    }
    align() {
      if (this.autoZIndex) {
        ZIndexUtils.set('overlay', this.container, this.baseZIndex + this.config.zIndex.overlay);
      }
      DomHandler.absolutePosition(this.container, this.target, false);
      const containerOffset = DomHandler.getOffset(this.container);
      const targetOffset = DomHandler.getOffset(this.target);
      const borderRadius = this.document.defaultView?.getComputedStyle(this.container).getPropertyValue('border-radius');
      let arrowLeft = 0;
      if (containerOffset.left < targetOffset.left) {
        arrowLeft = targetOffset.left - containerOffset.left - parseFloat(borderRadius) * 2;
      }
      this.container?.style.setProperty('--overlayArrowLeft', `${arrowLeft}px`);
      if (containerOffset.top < targetOffset.top) {
        DomHandler.addClass(this.container, 'p-overlaypanel-flipped');
        if (this.showCloseIcon) {
          this.renderer.setStyle(this.container, 'margin-top', '-30px');
        }
      }
    }
    onAnimationStart(event) {
      if (event.toState === 'open') {
        this.container = event.element;
        this.appendContainer();
        this.align();
        this.bindDocumentClickListener();
        this.bindDocumentResizeListener();
        this.bindScrollListener();
        if (this.closeOnEscape && this.showCloseIcon) {
          this.bindDocumentEscapeListener();
        }
        if (this.focusOnShow) {
          this.focus();
        }
        this.overlayEventListener = e => {
          if (this.container && this.container.contains(e.target)) {
            this.selfClick = true;
          }
        };
        this.overlaySubscription = this.overlayService.clickObservable.subscribe(this.overlayEventListener);
        this.onShow.emit(null);
      }
      this.isOverlayAnimationInProgress = true;
    }
    onAnimationEnd(event) {
      switch (event.toState) {
        case 'void':
          if (this.destroyCallback) {
            this.destroyCallback();
            this.destroyCallback = null;
          }
          if (this.overlaySubscription) {
            this.overlaySubscription.unsubscribe();
          }
          break;
        case 'close':
          if (this.autoZIndex) {
            ZIndexUtils.clear(this.container);
          }
          if (this.overlaySubscription) {
            this.overlaySubscription.unsubscribe();
          }
          this.onContainerDestroy();
          this.onHide.emit({});
          this.render = false;
          break;
      }
      this.isOverlayAnimationInProgress = false;
    }
    focus() {
      let focusable = DomHandler.findSingle(this.container, '[autofocus]');
      if (focusable) {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => focusable.focus(), 5);
        });
      }
    }
    /**
     * Hides the panel.
     * @group Method
     */
    hide() {
      this.overlayVisible = false;
      this.cd.markForCheck();
    }
    onCloseClick(event) {
      this.hide();
      event.preventDefault();
    }
    onEscapeKeydown(event) {
      this.hide();
    }
    onWindowResize() {
      if (this.overlayVisible && !DomHandler.isTouchDevice()) {
        this.hide();
      }
    }
    bindDocumentResizeListener() {
      if (isPlatformBrowser(this.platformId)) {
        if (!this.documentResizeListener) {
          const window = this.document.defaultView;
          this.documentResizeListener = this.renderer.listen(window, 'resize', this.onWindowResize.bind(this));
        }
      }
    }
    unbindDocumentResizeListener() {
      if (this.documentResizeListener) {
        this.documentResizeListener();
        this.documentResizeListener = null;
      }
    }
    bindScrollListener() {
      if (isPlatformBrowser(this.platformId)) {
        if (!this.scrollHandler) {
          this.scrollHandler = new ConnectedOverlayScrollHandler(this.target, () => {
            if (this.overlayVisible) {
              this.hide();
            }
          });
        }
        this.scrollHandler.bindScrollListener();
      }
    }
    bindDocumentEscapeListener() {
      const documentTarget = this.el ? this.el.nativeElement.ownerDocument : 'document';
      this.documentEscapeListener = this.renderer.listen(documentTarget, 'keydown', event => {
        if (event.which == 27) {
          this.onCloseClick(event);
        }
      });
    }
    unbindDocumentEscapeListener() {
      if (this.documentEscapeListener) {
        this.documentEscapeListener();
        this.documentEscapeListener = null;
      }
    }
    unbindScrollListener() {
      if (this.scrollHandler) {
        this.scrollHandler.unbindScrollListener();
      }
    }
    onContainerDestroy() {
      if (!this.cd.destroyed) {
        this.target = null;
      }
      this.unbindDocumentClickListener();
      this.unbindDocumentResizeListener();
      this.unbindScrollListener();
      this.unbindDocumentEscapeListener();
    }
    ngOnDestroy() {
      if (this.scrollHandler) {
        this.scrollHandler.destroy();
        this.scrollHandler = null;
      }
      if (this.container && this.autoZIndex) {
        ZIndexUtils.clear(this.container);
      }
      if (!this.cd.destroyed) {
        this.target = null;
      }
      this.destroyCallback = null;
      if (this.container) {
        this.restoreAppend();
        this.onContainerDestroy();
      }
      if (this.overlaySubscription) {
        this.overlaySubscription.unsubscribe();
      }
    }
    static ɵfac = function OverlayPanel_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || OverlayPanel)(i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1.PrimeNGConfig), i0.ɵɵdirectiveInject(i1.OverlayService));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: OverlayPanel,
      selectors: [["p-overlayPanel"]],
      contentQueries: function OverlayPanel_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, PrimeTemplate, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.templates = _t);
        }
      },
      hostAttrs: [1, "p-element"],
      hostBindings: function OverlayPanel_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("keydown.escape", function OverlayPanel_keydown_escape_HostBindingHandler($event) {
            return ctx.onEscapeKeydown($event);
          }, false, i0.ɵɵresolveDocument);
        }
      },
      inputs: {
        ariaLabel: "ariaLabel",
        ariaLabelledBy: "ariaLabelledBy",
        dismissable: [2, "dismissable", "dismissable", booleanAttribute],
        showCloseIcon: [2, "showCloseIcon", "showCloseIcon", booleanAttribute],
        style: "style",
        styleClass: "styleClass",
        appendTo: "appendTo",
        autoZIndex: [2, "autoZIndex", "autoZIndex", booleanAttribute],
        buttonTitle: "buttonTitle",
        baseZIndex: [2, "baseZIndex", "baseZIndex", numberAttribute],
        focusOnShow: [2, "focusOnShow", "focusOnShow", booleanAttribute],
        showTransitionOptions: "showTransitionOptions",
        closeOnEscape: "closeOnEscape",
        hideTransitionOptions: "hideTransitionOptions"
      },
      outputs: {
        onShow: "onShow",
        onHide: "onHide"
      },
      standalone: false,
      features: [i0.ɵɵInputTransformsFeature],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 1,
      consts: [[3, "ngClass", "ngStyle", "class", "click", 4, "ngIf"], [3, "click", "ngClass", "ngStyle"], ["autofocus", "", "tabindex", "0", 1, "p-overlaypanel-content", 3, "click", "mousedown"], [4, "ngTemplateOutlet"], ["type", "button", "class", "p-overlaypanel-close p-link", "tabindex", "0", "pRipple", "", 3, "click", "keydown.enter", 4, "ngIf"], ["type", "button", "tabindex", "0", "pRipple", "", 1, "p-overlaypanel-close", "p-link", 3, "click", "keydown.enter"], [1, "p-overlaypanel-close-icon", "pi", "pi-times"]],
      template: function OverlayPanel_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, OverlayPanel_div_0_Template, 5, 15, "div", 0);
        }
        if (rf & 2) {
          i0.ɵɵproperty("ngIf", ctx.render);
        }
      },
      dependencies: [i2.NgClass, i2.NgIf, i2.NgTemplateOutlet, i2.NgStyle, i3.Ripple],
      styles: [".p-overlaypanel{position:absolute;margin-top:10px;top:0;left:0}.p-overlaypanel-flipped{margin-top:0;margin-bottom:10px}.p-overlaypanel-close{display:flex;justify-content:center;align-items:center;overflow:hidden;position:relative}.p-overlaypanel:after,.p-overlaypanel:before{bottom:100%;left:calc(var(--overlayArrowLeft, 0) + 1.25rem);content:\" \";height:0;width:0;position:absolute;pointer-events:none}.p-overlaypanel:after{border-width:8px;margin-left:-8px}.p-overlaypanel:before{border-width:10px;margin-left:-10px}.p-overlaypanel-shifted:after,.p-overlaypanel-shifted:before{left:auto;right:1.25em;margin-left:auto}.p-overlaypanel-flipped:after,.p-overlaypanel-flipped:before{bottom:auto;top:100%}.p-overlaypanel.p-overlaypanel-flipped:after{border-bottom-color:transparent}.p-overlaypanel.p-overlaypanel-flipped:before{border-bottom-color:transparent}\n"],
      encapsulation: 2,
      data: {
        animation: [trigger('animation', [state('void', style({
          transform: 'scaleY(0.8)',
          opacity: 0
        })), state('close', style({
          opacity: 0
        })), state('open', style({
          transform: 'translateY(0)',
          opacity: 1
        })), transition('void => open', animate('{{showTransitionParams}}')), transition('open => close', animate('{{hideTransitionParams}}'))])]
      },
      changeDetection: 0
    });
  }
  return OverlayPanel;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let OverlayPanelModule = /*#__PURE__*/(() => {
  class OverlayPanelModule {
    static ɵfac = function OverlayPanelModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || OverlayPanelModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: OverlayPanelModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, RippleModule, SharedModule, TimesIcon, SharedModule]
    });
  }
  return OverlayPanelModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { OverlayPanel, OverlayPanelModule };
